// ============================================================================
// OMS Dashboard — full-width sales-order list. Row click routes to the detail
// sub-screen (/oms/:salesOrderId). Self-contained module.
// ============================================================================
import { DeploymentUnitOutlined } from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import React, { useState } from "react";
import ReconcileButton from "./components/reconcile_button";
import SalesOrderList from "./components/sales_order_list";
import SalesOrderListV1 from "./components/sales_order_list_v1";
import styles from "./index.module.scss";

type Version = "v1" | "v2";
const VERSION_KEY = "oms.overview.version";

const readVersion = (): Version => {
  try {
    return localStorage.getItem(VERSION_KEY) === "v2" ? "v2" : "v1";
  } catch {
    return "v1";
  }
};

const OmsDashboard: React.FC = () => {
  // Bumped after a reconcile so the list refetches and shows any leg that just moved.
  const [reloadKey, setReloadKey] = useState(0);
  const [version, setVersion] = useState<Version>(readVersion);

  const changeVersion = (v: Version) => {
    setVersion(v);
    try {
      localStorage.setItem(VERSION_KEY, v);
    } catch {
      // storage unavailable — the choice just isn't remembered
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <DeploymentUnitOutlined className={styles.headerIcon} />
        <h2 className={styles.title}>OMS — Order Fulfilment</h2>
        <Tooltip title={version === "v1" ? "Switch to V2" : "Switch to V1"}>
          <Button size="small" onClick={() => changeVersion(version === "v1" ? "v2" : "v1")}>
            {version === "v1" ? "V1" : "V2"}
          </Button>
        </Tooltip>
        <div className={styles.headerActions}>
          <ReconcileButton onDone={() => setReloadKey((k) => k + 1)} />
        </div>
      </div>
      {/* The list opens each order in a NEW tab on row click, so the dashboard keeps its scroll
          position / page / filters — ops scans it like a work queue and returns to the same spot. */}
      {version === "v1" ? (
        <SalesOrderListV1 key={`v1-${reloadKey}`} />
      ) : (
        <SalesOrderList key={`v2-${reloadKey}`} />
      )}
    </div>
  );
};

export default OmsDashboard;
